import { Col, Row } from "react-grid-system";
import leftArrow from "../../../assets/left-blue-pagination-arrow.svg";
import { GetPhoneOptionsResponse, PhoneContact } from "../../../lib/api/models";
import { formatInCentralTime } from "../../../lib/formatting";
import StyledButton from "../../common/buttons/StyledButton";
import TextButton from "../../common/buttons/TextButton";
import MemoHistory from "../MemoHistory";
import PhoneNumberDisplay from "../PhoneNumberDisplay";
import VerifyPhone from "../VerifyPhone";
import ViewPhoneActivationStatus from "../ViewPhoneActivationStatus";
import ViewPhoneRestriction from "../ViewPhoneRestriction";
import toolStyles from "../../../sharedStyles/Tool.module.css";
import styles from "./ViewPhoneNumber.module.css";

const strings = {
  owner: "Owner",
  type: "Type",
  firstName: "First Name",
  lastName: "Last Name",
  suffix: "Suffix",
  source: "Source",
  autoSystemsConsent: "Automated Systems Consent",
  autosysQuestion:
    "Can we contact you at this number using an automatic dialer and artificial and prerecorded voice?",
  lastConsent: "Last Consent",
  lastMethod: "Last Method",
  lastUpdated: "Last Updated",
  restrictedUse: (s: string) => `Restricted Use: ${s}`,
  restrictedDays: "Restricted Days",
  callFrequency: "Call Frequency",
  restrictionLiftDate: "Restriction Lift Date",
  status: "Status:",
  active: "Active",
  title: "View Number",
  back: "Back to Number List",
  edit: "Edit",
  close: "Close",
  addMemo: "Add Memo",
  memoPlaceholder: "Enter Memo",
  memos: "Memos",
};

type Props = {
  phoneContact: PhoneContact;
  options: GetPhoneOptionsResponse;
  onClickClose: () => void;
  onClickEdit: (phoneContact: PhoneContact) => void;
  onClickCancel: () => void;
  onVerify: (phoneId: string) => void;
  isVerifyLoading: boolean;
};

export default function ViewPhoneNumber({
  phoneContact,
  options,
  onClickClose,
  onClickEdit,
  onClickCancel,
  onVerify,
  isVerifyLoading,
}: Props) {
  return (
    <>
      <Row className={styles.rowSpacing}>
        <Col md={2}>
          <h2 className={toolStyles.title}>{strings.title}</h2>
          <TextButton className={styles.backButton} onClick={onClickCancel}>
            <img
              className={styles.leftArrow}
              src={leftArrow}
              alt="back arrow"
            />
            {strings.back}
          </TextButton>
        </Col>
        <Col md={4}>
          <PhoneNumberDisplay phoneContact={phoneContact} />
        </Col>
        <Col offset={{ md: 4 }} md={1}>
          <StyledButton
            variant="secondary"
            className={styles.wFull}
            onClick={() => onClickEdit(phoneContact)}
          >
            {strings.edit}
          </StyledButton>
        </Col>
        <Col md={1}>
          <StyledButton
            variant="secondary"
            className={styles.wFull}
            onClick={() => onClickClose()}
          >
            {strings.close}
          </StyledButton>
        </Col>
      </Row>
      <Row className={styles.rowSpacing}>
        <Col md={3}>
          <div className={styles.ownershipContainer}>
            <div className={styles.ownerRow}>
              <span className={styles.ownerLabel}>{strings.owner}</span>
              <span className={styles.ownerValue}>
                {
                  options.phoneOwnerTypes.find(
                    (ot) => ot.id === phoneContact.ownerType,
                  )?.value
                }
              </span>
            </div>
            <div className={styles.ownerRow}>
              <span className={styles.ownerLabel}>{strings.type}</span>
              <span className={styles.ownerValue}>
                {
                  options.phoneTypes.find(
                    (pt) => pt.id === phoneContact.phoneType,
                  )?.value
                }
              </span>
            </div>
            <div className={styles.ownerRow}>
              <span className={styles.ownerLabel}>{strings.firstName}</span>
              <span className={styles.ownerValue}>
                {phoneContact.ownerFirstName}
              </span>
            </div>
            <div className={styles.ownerRow}>
              <span className={styles.ownerLabel}>{strings.lastName}</span>
              <span className={styles.ownerValue}>
                {phoneContact.ownerLastName}
              </span>
            </div>
            <div className={styles.ownerRow}>
              <span className={styles.ownerLabel}>{strings.suffix}</span>
              <span className={styles.ownerValue}>
                {
                  options.nameSuffixes.find(
                    (ns) => ns.id === phoneContact.ownerSuffix,
                  )?.value
                }
              </span>
            </div>
            <div className={styles.sourceRow}>
              <span className={styles.ownerLabel}>{strings.source}</span>
              <div className={styles.sourceList}>
                {phoneContact.sourceType.map((st, index) => {
                  const source =
                    options.phoneSources.find((ps) => ps.id === st.source)
                      ?.value ?? st.source;
                  return (
                    <span
                      key={`${index}-${st.source}`}
                      className={styles.ownerValue}
                    >
                      {`${source} (${st.count})`}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </Col>
        <Col md={3}>
          <div className={styles.autosysContainer}>
            <div className={styles.autosysSubheader}>
              {strings.autoSystemsConsent}
            </div>
            <div className={styles.autosysQuestion}>
              {strings.autosysQuestion}
            </div>
            <div className={styles.autosysLabeledValues}>
              <div className={styles.autosysRow}>
                <span className={styles.autosysLabel}>
                  {strings.lastConsent}
                </span>
                <span className={styles.autosysValue}>
                  {phoneContact.consentStatus
                    ? options.pecStatusTypes.find(
                        (st) => st.id === phoneContact.consentStatus,
                      )?.value
                    : "-"}
                </span>
              </div>
              <div className={styles.autosysRow}>
                <span className={styles.autosysLabel}>
                  {strings.lastMethod}
                </span>
                <span className={styles.autosysValue}>
                  {phoneContact.consentSourceType
                    ? options.pecSources.find(
                        (source) =>
                          source.id === phoneContact.consentSourceType,
                      )?.value
                    : "-"}
                </span>
              </div>
              <div className={styles.autosysRow}>
                <span className={styles.autosysLabel}>
                  {strings.lastUpdated}
                </span>
                <span className={styles.autosysValue}>
                  {phoneContact.consentCapturedDate
                    ? formatInCentralTime(
                        phoneContact.consentCapturedDate,
                        "MM/dd/yyyy",
                      )
                    : "-"}
                </span>
              </div>
            </div>
          </div>
        </Col>
        <Col md={6}>
          <div className={styles.rightSection}>
            <VerifyPhone
              lastVerified={
                phoneContact.lastVerifiedDate
                  ? formatInCentralTime(
                      phoneContact.lastVerifiedDate,
                      "MM/dd/yyyy",
                    )
                  : "-"
              }
              lastCaptured={formatInCentralTime(
                phoneContact.lastCapturedDate,
                "MM/dd/yyyy",
              )}
              firstCaptured={formatInCentralTime(
                phoneContact.firstCapturedDate,
                "MM/dd/yyyy",
              )}
              onVerify={() => onVerify(phoneContact.phoneID)}
              isLoading={isVerifyLoading}
            />
            <div className={styles.restrictionsAndStatusContainer}>
              <ViewPhoneRestriction
                options={options}
                restriction={phoneContact.restriction}
                className={styles.restrictionsContainer}
              />
              <ViewPhoneActivationStatus
                inactivation={phoneContact.inactivation}
                options={options}
                className={styles.statusContainer}
              />
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={6} offset={{ md: 6 }}>
          <MemoHistory memos={phoneContact.memos} header={strings.memos} />
        </Col>
      </Row>
    </>
  );
}
